import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePostComments, usePostActions } from '@/hooks/usePosts';
import CommentItem from './CommentItem';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { MessageCircle, LogIn, Send } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';

interface CommentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  petName?: string;
}

const CommentsModal = ({ isOpen, onClose, postId, petName }: CommentsModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { data: comments, isLoading, refetch } = usePostComments(isOpen ? postId : undefined);
  const { addComment, isCommenting } = usePostActions();
  const [commentText, setCommentText] = useState('');

  const title = petName
    ? t('comments.commentsOn', { name: petName, defaultValue: `Comentarios sobre ${petName}` })
    : t('comments.title', { defaultValue: 'Comentarios' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !postId) return;
    try {
      await addComment({ postId, content: commentText });
      setCommentText('');
      toast({
        title: t('comments.commentAdded'),
        description: t('comments.addedSuccess'),
      });
      refetch();
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.message || t('comments.addError'),
        variant: 'destructive',
      });
    }
  };

  const list = (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
      {isLoading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex mb-4">
            <Skeleton className="h-8 w-8 rounded-full mr-3" />
            <div className="flex-1">
              <Skeleton className="h-4 w-24 mb-1" />
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
        ))
      ) : comments && comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((c: any) => (
            <CommentItem
              key={c.id}
              comment={c}
              postId={postId}
              onReplyAdded={() => refetch()}
            />
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-muted-foreground py-6">
          {t('comments.noComments', { defaultValue: 'No comments yet' })}
        </p>
      )}
    </div>
  );

  const footer = user ? (
    <form
      onSubmit={handleSubmit}
      className="flex items-center gap-2 px-4 py-3 border-t bg-background shrink-0"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
    >
      <input
        type="text"
        placeholder={t('comments.addComment', { defaultValue: 'Add a comment...' })}
        className="flex-1 bg-transparent text-sm outline-none min-w-0"
        value={commentText}
        onChange={(e) => setCommentText(e.target.value)}
        disabled={isCommenting}
      />
      <Button
        type="submit"
        className="rounded-full h-10 w-10 flex items-center justify-center bg-pet-purple hover:bg-pet-purple/90 text-primary-foreground shrink-0 p-0"
        disabled={!commentText.trim() || isCommenting}
      >
        <Send className="h-4 w-4" />
      </Button>
    </form>
  ) : (
    <div className="px-4 py-3 border-t text-center shrink-0">
      <Button size="sm" asChild variant="outline">
        <Link to="/auth" onClick={onClose}>
          <LogIn className="h-4 w-4 mr-2" />
          {t('auth.signIn', { defaultValue: 'Iniciar sesión' })}
        </Link>
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={(o) => !o && onClose()}>
        <SheetContent
          side="bottom"
          className="p-0 h-auto min-h-[35dvh] max-h-[85dvh] flex flex-col rounded-t-2xl overflow-hidden"
        >
          <SheetHeader className="px-4 pt-4 pb-2 border-b shrink-0">
            <SheetTitle className="flex items-center gap-2 text-base">
              <MessageCircle className="h-5 w-5" />
              {title}
            </SheetTitle>
          </SheetHeader>
          {list}
          {footer}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageCircle className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex mb-4">
                <Skeleton className="h-8 w-8 rounded-full mr-3" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-24 mb-1" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))
          ) : comments && comments.length > 0 ? (
            <div className="space-y-4">
              {comments.map((c: any) => (
                <CommentItem
                  key={c.id}
                  comment={c}
                  postId={postId}
                  onReplyAdded={() => refetch()}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-sm text-muted-foreground py-4">
              {t('comments.noComments', { defaultValue: 'No comments yet' })}
            </p>
          )}
        </div>
        {footer}
      </DialogContent>
    </Dialog>
  );
};

export default CommentsModal;
